// src/pages/appointment-calendar/components/MobileCalendarView.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Select from "../../../components/ui/Select";
import MobileResourceGrid from "./MobileResourceGrid";

const normalizeStatus = (status) => {
  switch (status) {
    case "booked":
      return "confirmed";
    case "cancelled":
      return "cancelled";
    case "completed":
      return "completed";
    default:
      return "pending";
  }
};

const getStatusColor = (status) => {
  const colors = {
    pending: "bg-warning",
    confirmed: "bg-success",
    cancelled: "bg-error",
    completed: "bg-primary",
    noshow: "bg-secondary",
  };
  return colors[status] || colors.pending;
};

const extractPrice = (raw) => {
  if (!raw) return null;
  const val =
    raw.total_price ??
    raw.price ??
    raw.service_price ??
    (raw.service && raw.service.price);

  const n = parseFloat(val || 0);
  return Number.isFinite(n) ? n : null;
};

const formatDateForPayload = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const MobileCalendarView = ({
  currentDate,
  view = "day",
  onViewChange,
  appointments = [],
  onAppointmentClick,
  onDateChange,
  onDateSelect,
  onOpenDatePicker,
  // legacy (desktop uses single-select); pe mobil folosim selectedStaffIds
  selectedStaff,
  staffOptions = [],
  selectedStaffIds = [],
  onSelectedStaffIdsChange,
  onCreateAppointment,
  workingHours,
  slotMinutes = 15,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(currentDate || new Date());
  const [staffPickError, setStaffPickError] = useState("");

  // Sync selectedDate when parent changes currentDate
  useEffect(() => {
    if (currentDate) setSelectedDate(currentDate);
  }, [currentDate]);

  const isToday = (date) => {
    const today = new Date();
    return date?.toDateString() === today.toDateString();
  };

  const goToToday = () => {
    const today = new Date();
    setSelectedDate(today);
    onDateChange?.(today);
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);

    if (view === "week") {
      newDate.setDate(newDate.getDate() + (direction === "next" ? 7 : -7));
    } else if (view === "month") {
      newDate.setMonth(newDate.getMonth() + (direction === "next" ? 1 : -1));
    } else {
      newDate.setDate(newDate.getDate() + (direction === "next" ? 1 : -1));
    }

    setSelectedDate(newDate);
    onDateChange?.(newDate);
  };

  const goToDay = (date) => {
    if (!date) return;
    const d = new Date(date);
    setSelectedDate(d);
    onDateChange?.(d);     // update parent currentDate
    onViewChange?.("day"); // treci pe tabul Zi
  };

  const startOfWeek = (date) => {
    const d = new Date(date);
    const wd = (d.getDay() + 6) % 7; // luni = 0
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - wd);
    return d;
  };

  const weekDays = (date) => {
    const start = startOfWeek(date);
    return Array.from({ length: 7 }).map((_, i) => {
      const x = new Date(start);
      x.setDate(start.getDate() + i);
      return x;
    });
  };

  const isSameDay = (a, b) => {
    if (!a || !b) return false;
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  };

  // Month grid helpers (Lu...Du)
  const monthWeekDays = t("calendar.weekDaysShort", { returnObjects: true });
  const mondayIndex = (date) => (date.getDay() + 6) % 7;

  const buildMonthGrid = (anchorDate) => {
    const y = anchorDate.getFullYear();
    const m = anchorDate.getMonth();
    const first = new Date(y, m, 1);

    const start = new Date(first);
    start.setHours(0, 0, 0, 0);
    start.setDate(first.getDate() - mondayIndex(first)); // luni start

    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push({
        date: d,
        inMonth: d.getMonth() === m,
      });
    }
    return cells;
  };

  const headerMonthLabel = useMemo(() => {
    return selectedDate?.toLocaleDateString(i18n.language || "ro", {
      month: "short",
      year: "numeric",
    });
  }, [selectedDate, i18n.language]);

  const headerDayLabel = useMemo(() => {
    return selectedDate?.toLocaleDateString(i18n.language || "ro", {
      weekday: "long",
      day: "numeric",
      month: "short",
    });
  }, [selectedDate, i18n.language]);

  // --- Multi-select staff pe mobil ---
  const staffSelected = useMemo(() => {
    const arr = Array.isArray(selectedStaffIds)
      ? selectedStaffIds
      : selectedStaff && selectedStaff !== "all"
        ? [selectedStaff]
        : [];
    return arr.map(String).filter(Boolean);
  }, [selectedStaffIds, selectedStaff]);

  const allStaffIds = useMemo(
    () => (Array.isArray(staffOptions) ? staffOptions.map((s) => String(s.id)) : []),
    [staffOptions]
  );

  const effectiveStaffIds = staffSelected.length ? staffSelected : allStaffIds;
  const needsFilter = !staffSelected.length && allStaffIds.length > 12;
  const tooManySelected = staffSelected.length > 12;
  const showGrid =
    view === "day" && !needsFilter && !tooManySelected && effectiveStaffIds.length > 0;

  const staffSelectOptions = useMemo(
    () =>
      (Array.isArray(staffOptions) ? staffOptions : []).map((u) => ({
        value: String(u.id),
        label: u.label || `#${u.id}`,
      })),
    [staffOptions]
  );

  const handleStaffMultiSelect = (val) => {
    const arr = Array.isArray(val) ? val.map(String).filter(Boolean) : [];
    if (arr.length > 12) {
      setStaffPickError(t("calendar.maxStaffMobile"));
      onSelectedStaffIdsChange?.(arr.slice(0, 12));
      return;
    }
    if (staffPickError) setStaffPickError("");
    onSelectedStaffIdsChange?.(arr);
  };

  const getAppointmentsForDate = (date) => {
    if (!date) return [];

    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);

    return (appointments || [])
      .filter((apt) => {
        if (!apt?.start) return false;

        const s = apt.start instanceof Date ? apt.start : new Date(apt.start);
        const matchesDate = s >= start && s < end;

        if (needsFilter) return false;

        const staffId = apt._raw?.staff_id ?? apt._raw?.staff ?? apt.staffId ?? null;

        const matchesStaff =
          !staffSelected.length || (staffId != null && staffSelected.includes(String(staffId)));

        return matchesDate && matchesStaff;
      })
      .map((apt) => {
        const raw = apt._raw || {};
        const startDate = apt.start instanceof Date ? apt.start : new Date(apt.start);
        const endDate =
          apt.end instanceof Date ? apt.end : apt.end ? new Date(apt.end) : startDate;

        const durationMinutes = Math.max(0, Math.round((endDate - startDate) / 60000) || 0);

        const price = extractPrice(raw);
        const status = normalizeStatus(apt.status || raw.status);

        return {
          ...apt,
          start: startDate,
          end: endDate,
          duration: durationMinutes,
          price,
          status,
        };
      })
      .sort((a, b) => a.start - b.start);
  };

  // counts reale pe lună (din appointments)
  const monthCounts = useMemo(() => {
    if (needsFilter) return {};

    const counts = {};
    const selectedSet = new Set(staffSelected);
    const filterByStaff = staffSelected.length > 0;

    for (const apt of appointments || []) {
      const s = apt?.start instanceof Date ? apt.start : new Date(apt?.start);
      if (!s || Number.isNaN(s.getTime())) continue;

      const staffId = apt?._raw?.staff_id ?? apt?._raw?.staff ?? apt?.staffId ?? null;
      if (filterByStaff) {
        if (staffId == null || !selectedSet.has(String(staffId))) continue;
      }

      const k = formatDateForPayload(s); // YYYY-MM-DD
      counts[k] = (counts[k] || 0) + 1;
    }

    return counts;
  }, [appointments, needsFilter, staffSelected.join("|")]);

  const triggerCreateAppointment = () => {
    if (!onCreateAppointment) return;

    const oneStaff = staffSelected.length === 1 ? staffSelected[0] : "";

    const payload = {
      date: formatDateForPayload(selectedDate || new Date()),
      staffId: oneStaff || "",
      // compat: unele locuri încă citesc `staff`
      staff: oneStaff || "",
    };
    onCreateAppointment(payload);
  };

  const SegButton = ({ label, value }) => {
    const active = view === value;
    return (
      <button
        type="button"
        onClick={() => onViewChange?.(value)}
        className={[
          "px-3 h-8 text-xs font-medium rounded-md transition-colors",
          active ? "bg-card text-foreground shadow-sm" : "text-text-secondary hover:text-foreground",
        ].join(" ")}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="lg:hidden">
      {/* Header */}
      <div className="bg-background border-b border-border px-2 py-3">
        {/* Date navigation */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateDate("prev")}
            className="w-9 h-9"
            type="button"
            title={t("calendar.previous")}
          >
            <Icon name="ChevronLeft" size={18} />
          </Button>

          <div className="text-center min-w-[120px]">
            <div className="text-lg font-semibold text-foreground leading-tight">
              {selectedDate?.getDate()}
            </div>
            <div className="text-sm text-text-secondary leading-tight">{headerMonthLabel}</div>
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateDate("next")}
            className="w-9 h-9"
            type="button"
            title={t("calendar.next")}
          >
            <Icon name="ChevronRight" size={18} />
          </Button>
        </div>

        {/* View toggle + calendar picker */}
        <div className="flex items-center justify-between mb-3">
          <div className="inline-flex rounded-lg bg-muted p-1">
            <SegButton label={t("calendar.day")} value="day" />
            <SegButton label={t("calendar.weekShort")} value="week" />
            <SegButton label={t("calendar.monthShort")} value="month" />
          </div>

          <Button
            variant="outline"
            size="icon"
            type="button"
            onClick={() => onOpenDatePicker?.()}
            className="w-9 h-9"
            title={t("calendar.pickDate")}
          >
            <Icon name="Calendar" size={16} />
          </Button>
        </div>

        {/* Day label + Today */}
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-text-secondary capitalize truncate">{headerDayLabel}</div>

          {!isToday(selectedDate) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={goToToday}
              className="text-xs whitespace-nowrap"
              type="button"
            >
              {t("calendar.todayBtn")}
            </Button>
          )}
        </div>

        {/* Staff multi-select (Mobile) */}
        <div className="mt-3">
          <Select
            label={t("appointments.staffLabel")}
            options={staffSelectOptions}
            multiple
            searchable
            clearable
            placeholder={allStaffIds.length > 12 ? t("calendar.pickStaff12") : t("calendar.allStaff")}
            value={staffSelected}
            onChange={handleStaffMultiSelect}
          />

          {staffPickError ? <div className="mt-1 text-xs text-red-600">{staffPickError}</div> : null}

          {needsFilter ? (
            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
              {t("calendar.tooManyStaff", { count: allStaffIds.length })}
            </div>
          ) : null}
        </div>
      </div>

      {/* Content */}
      <div className="px-2 py-3">

        {view === "week" ? (
          <div className="space-y-6">
            {weekDays(selectedDate).map((d) => {
              const items = getAppointmentsForDate(d);
              const label = d.toLocaleDateString(i18n.language || "ro", {
                weekday: "long",
                day: "numeric",
                month: "short",
              });

              return (
                <div key={formatDateForPayload(d)}>
                  <div className="flex items-center justify-between mb-2">
                    <button
                      type="button"
                      className="text-sm font-semibold text-foreground capitalize text-left"
                      onClick={() => goToDay(d)}
                      title={t("calendar.viewDay")}
                    >
                      {label}
                    </button>
                    <div className="text-xs text-text-secondary">
                      {items.length ? t("calendar.nAppointments", { count: items.length }) : "—"}
                    </div>
                  </div>

                  {items.length ? (
                    <div className="space-y-3">
                      {items.map((appointment) => (
                        <button
                          key={appointment.id}
                          type="button"
                          className="w-full text-left py-3 border-b border-border/60 active:bg-muted/50"
                          onClick={() => onAppointmentClick?.(appointment)}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-foreground truncate">
                                {appointment.clientName || "—"}
                              </div>
                              <div className="text-xs text-text-secondary truncate">
                                {appointment.serviceName || "—"} · {appointment.staffName || "—"}
                              </div>
                            </div>

                            <span
                              className={`shrink-0 inline-block w-2.5 h-2.5 rounded-full ${getStatusColor(
                                appointment.status
                              )}`}
                              title={appointment.status}
                            />
                          </div>
                        </button>

                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-text-secondary">{t("calendar.noAppointments")}</div>
                  )}
                </div>
              );
            })}
          </div>
        ) : view === "day" ? (
          <div className="-mx-2">
            {!staffOptions?.length ? (
              <div className="text-center py-10 text-sm text-text-secondary">
                {t("calendar.loadingStaff")}
              </div>
            ) : showGrid ? (
              <div className="w-full">
                <MobileResourceGrid
                  date={selectedDate}
                  appointments={appointments}
                  staff={staffOptions}
                  staffIds={effectiveStaffIds}
                  scroll={effectiveStaffIds.length > 6}
                  onAppointmentClick={onAppointmentClick}
                  onDateSelect={onDateSelect}
                  workingHours={workingHours}
                  slotMinutes={slotMinutes}
                />
              </div>
            ) : (
              <div className="border-t border-border/60 py-4 text-sm text-text-secondary px-2">
                {t("calendar.selectMax12")}
              </div>
            )}
          </div>

        ) : view === "month" ? (
          <div className="space-y-3">
            {/* header zile saptamana */}
            <div className="grid grid-cols-7 text-xs text-text-secondary">
              {monthWeekDays.map((wd) => (
                <div key={wd} className="text-center py-1">
                  {wd}
                </div>
              ))}
            </div>

            {/* grila 6 saptamani */}
            <div className="grid grid-cols-7 gap-1">
              {buildMonthGrid(selectedDate).map((cell) => {
                const key = formatDateForPayload(cell.date);
                const count = monthCounts[key] || 0;
                const selected = isSameDay(cell.date, selectedDate);
                const today = isSameDay(cell.date, new Date());

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => goToDay(cell.date)}
                    className={[
                      "relative h-11 border border-border/60 bg-background flex flex-col items-center justify-center",
                      "transition active:bg-muted/50",
                      cell.inMonth ? "opacity-100" : "opacity-40",
                      selected ? "ring-2 ring-primary" : "",
                      !selected && today ? "ring-1 ring-primary/30" : "",
                    ].join(" ")}

                    title="Vezi ziua"
                  >
                    <div className="text-sm font-medium text-foreground">{cell.date.getDate()}</div>

                    {count > 0 ? (
                      <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[11px] leading-none flex items-center justify-center">
                        {count}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            <div className="text-xs text-text-secondary">{t("calendar.tapDayHint")}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MobileCalendarView;
